/**
 * ShareView — Leseansicht ohne Anmeldung (`/share/<id>`).
 *
 * Zeigt einen Beitrag, den content-api anonym ausliefert: veröffentlicht
 * und mit Sichtbarkeit „öffentlich" (oder ein von sich aus öffentlicher
 * Dokumenttyp). Alles andere antwortet mit 404 und landet hier als
 * „nicht öffentlich".
 *
 * Bis 03.10.2026 fragte diese Seite zuerst nach einem Namen und bot ein
 * Gast-Anmerken an. Anmerken verlangt seit der Rollenumstellung eine
 * Anmeldung, und Anmerkungen gehen nie an anonyme Leser; beides ist hier
 * deshalb entfernt. Wer anmerken will, meldet sich an und öffnet den
 * Beitrag im Portal.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../api/client'
import { getAccessToken } from '../auth'
import { parseMarkdown } from '../utils/markdownConfig'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { useD2Async } from '../hooks/useD2Async'
import '../styles/global.css'

/** Ein führendes H1, das den Titel wiederholt, steht schon als Seitentitel da. */
function stripTitleHeading(md: string, title: string): string {
  const firstLine = md.split('\n')[0]?.trim() || ''
  if (!firstLine.startsWith('# ')) return md
  const h1 = firstLine.slice(2).trim().toLowerCase()
  const t = title.trim().toLowerCase()
  if (!t.startsWith(h1) && !h1.startsWith(t)) return md
  return md.split('\n').slice(1).join('\n').replace(/^\n+/, '')
}

export function ShareView() {
  const { id } = useParams<{ id: string }>()
  const [readModeRoot, setReadModeRoot] = useState<HTMLElement | null>(null)
  useMermaidAsync(readModeRoot)
  useD2Async(readModeRoot)

  const { data: post, isLoading } = useQuery({
    queryKey: ['share-post', id],
    queryFn: () => apiClient.getPost(Number(id)),
    enabled: !!id,
    retry: false,
  })

  useEffect(() => {
    if (post?.title) document.title = post.title
  }, [post?.title])

  const html = useMemo(
    () => (post ? { __html: parseMarkdown(stripTitleHeading(post.content || '', post.title)) } : { __html: '' }),
    [post],
  )
  const loggedIn = !!getAccessToken()

  if (isLoading) return <div className="share-loading">Lädt …</div>
  if (!post) {
    return (
      <div className="share-loading">
        <p>Dieses Dokument ist nicht öffentlich oder existiert nicht.</p>
        <p><Link to={`/post/${id}`}>Anmelden und im Portal öffnen</Link></p>
      </div>
    )
  }

  return (
    <div className="share-view">
      <header className="share-header">
        <span className="share-logo">Content</span>
        <span className="share-meta">
          {post.author_name} · {new Date(post.updated_at || post.created_at).toLocaleDateString('de-AT')}
        </span>
        <span style={{ flex: 1 }} />
        <Link className="share-user" to={`/post/${post.id}`} title={loggedIn ? 'Im Portal öffnen' : 'Anmelden, um anzumerken'}>
          <i className={`fas ${loggedIn ? 'fa-arrow-up-right-from-square' : 'fa-right-to-bracket'}`} style={{ marginRight: 5 }} />
          {loggedIn ? 'Im Portal öffnen' : 'Anmelden'}
        </Link>
      </header>
      <main className="share-body">
        <article className="share-content">
          <h1 className="share-title">{post.title}</h1>
          {post.subtitle && <p className="share-subtitle">{post.subtitle}</p>}
          <div ref={setReadModeRoot} className="editor-content" dangerouslySetInnerHTML={html} />
        </article>
      </main>
    </div>
  )
}
